import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { BdAppConfigResponse } from '@bindoc-projects/app-config-domain-lib';
import {
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
} from '@azure/msal-angular';
import { storeRedirectTarget } from './redirect-target';
import { preventLoginAfterFailedRedirect } from './failed-redirect-guard';
import {
  extractAuthorityMetadata,
  serveInlinedDiscovery,
} from './authority-metadata';

export class BdAppConfigResponseHelper {
  public static msalInstance(
    appConfigResponse: BdAppConfigResponse,
  ): IPublicClientApplication {
    // The inlined discovery document belongs to the sign-in policy only; see
    // authority-metadata.ts (BDA-6509).
    const { configuration, discovery } = extractAuthorityMetadata(
      appConfigResponse.msalConfig.publicClientApplication,
    );
    const instance = new PublicClientApplication(configuration);
    serveInlinedDiscovery(instance, discovery);
    // Record the originally requested URL right before MSAL redirects to the
    // authority, so the app can restore it via the router after the response
    // is processed. BdAuthenticationRoot disables MSAL's own hard-navigation
    // restore (`handleRedirectObservable({ navigateToLoginRequestUrl: false })`)
    // and replays this URL client-side instead — see redirect-target.ts.
    instance.addEventCallback(storeRedirectTarget);
    // Keep the BDA-5866 terminal failure gate. MSAL 5.14 handles concurrent
    // redirects itself; see msal-redirect-regression.spec.ts and README.md.
    return preventLoginAfterFailedRedirect(instance);
  }

  public static msalInterceptorConfig(
    appConfigResponse: BdAppConfigResponse,
  ): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();

    appConfigResponse.msalConfig.protectedResources.forEach(
      (protectedResource) => {
        // Tolerate malformed entries from outdated frontend-config deployments:
        // a missing `uri` would otherwise crash the whole app at bootstrap.
        if (!protectedResource?.uri) {
          console.warn(
            'Skipping protectedResources entry with missing uri',
            protectedResource,
          );
          return;
        }
        // MSAL Angular v5 uses strict matching by default.
        // Angular's Location.normalize() strips trailing slashes, so requests
        // to base URLs (e.g. code-spectra/) won't match a wildcard-only entry.
        // Register both an exact match and a wildcard to cover all cases.
        const normalizedUri = protectedResource.uri.endsWith('/')
          ? protectedResource.uri.slice(0, -1)
          : protectedResource.uri;
        protectedResourceMap.set(normalizedUri, protectedResource.scopes);
        protectedResourceMap.set(
          normalizedUri + '/*',
          protectedResource.scopes,
        );
      },
    );

    // Use Popup interaction inside iframes to prevent BrowserAuthError: block_iframe_reload.
    // In the normal (non-iframe) case, continue using Redirect.
    const interactionType =
      typeof window !== 'undefined' &&
      window !== window.parent &&
      !window.opener
        ? InteractionType.Popup
        : InteractionType.Redirect;

    // When a dedicated lightweight redirect page is configured (and registered
    // as SPA redirect URI in the B2C app registration), route the
    // interceptor's token requests through it: silent renewals then load a
    // static MSAL redirect-bridge page in the hidden iframe instead of the whole SPA.
    // See the `silentRedirectUri` docs in app-config-domain-lib.
    const silentRedirectUri = appConfigResponse.msalConfig.silentRedirectUri;

    return {
      interactionType,
      protectedResourceMap,
      ...(silentRedirectUri
        ? { authRequest: { redirectUri: silentRedirectUri } }
        : {}),
    };
  }

  public static msalGuardConfig(
    appConfigResponse: BdAppConfigResponse,
  ): MsalGuardConfiguration {
    return {
      interactionType:
        appConfigResponse.msalConfig.guardConfig.interactionType === 'redirect'
          ? InteractionType.Redirect
          : InteractionType.Popup,
      authRequest: appConfigResponse.msalConfig.guardConfig.authRequest,
      loginFailedRoute:
        appConfigResponse.msalConfig.guardConfig?.loginFailedRoute ??
        'sys-login-failed',
    };
  }
}
